import { Typography } from '@mui/joy';
import { CloudArrowUp } from '@phosphor-icons/react';
import * as fileUpload from '@zag-js/file-upload';
import {
  PropTypes,
  mergeProps,
  normalizeProps,
  useMachine,
} from '@zag-js/react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { z } from 'zod';

import { Button } from './button';
import { createSafeContext, createSplitProps } from './utils';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;
type UseFilePickerProps = Optional<fileUpload.Context, 'id'>;
type UseFilePickerReturn = fileUpload.Api<PropTypes>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

type Options = {
  /**
   * Whether to use flushSync or not
   */
  sync?: boolean;
};

/**
 * Returns a memoized callback that will flushSync the callback if sync is true
 */
function useEvent<T extends AnyFunction>(
  callback: T | undefined,
  opts: Options = {},
): T {
  const { sync = false } = opts;

  const callbackRef = useLatestRef(callback);

  return useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args: any[]) => {
      if (sync) return queueMicrotask(() => callbackRef.current?.(...args));
      return callbackRef.current?.(...args);
    },
    [sync, callbackRef],
  ) as T;
}

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

const [Provider, useFilePicker] = createSafeContext<UseFilePickerReturn>(
  'useFilePicker must be used within a FileUploadProvider',
);
export { useFilePicker };
export function FilePicker(
  props: React.ComponentPropsWithoutRef<'div'> & UseFilePickerProps,
) {
  const [useFileUploadProps, localProps] =
    createSplitProps<UseFilePickerProps>()(props, [
      'dir',
      'getRootNode',
      'accept',
      'allowDrop',
      'capture',
      'directory',
      'disabled',
      'id',
      'ids',
      'locale',
      'maxFiles',
      'maxFileSize',
      'minFileSize',
      'name',
      'onFileAccept',
      'onFileChange',
      'onFileReject',
      'translations',
      'validate',
    ]);

  const initialContext: fileUpload.Context = {
    id: useId(),
    ...useFileUploadProps,
  };

  const context: fileUpload.Context = {
    ...initialContext,
    onFileAccept: useEvent(useFileUploadProps.onFileAccept),
    onFileReject: useEvent(useFileUploadProps.onFileReject),
    onFileChange: useEvent(useFileUploadProps.onFileChange, { sync: true }),
  };

  const [state, send] = useMachine(fileUpload.machine(initialContext), {
    context,
  });

  const api = fileUpload.connect(state, send, normalizeProps);
  const mergedProps = mergeProps(api.rootProps, localProps);

  return (
    <Provider value={api}>
      <div
        {...mergedProps}
        data-empty-selection={api.acceptedFiles.length === 0 ? '' : undefined}
        className={clsx([mergedProps.className, 'group'])}
      />
    </Provider>
  );
}

export function FilePickerDropzone() {
  const api = useFilePicker();
  return (
    <div className='border border-dashed border-brand-primary flex flex-col bg-brand-primary-50 rounded-lg min-h-40'>
      <div
        {...api.dropzoneProps}
        className='w-full flex-1 flex items-center justify-center p-4'
      >
        <div className='flex flex-col gap-2 items-center'>
          <CloudArrowUp className='text-brand-primary-500 text-4xl' />
          <input {...api.hiddenInputProps} />
          <span className='text-base/6 text-zinc-400'>
            Drag or Drop Files here or{' '}
            <button
              {...api.triggerProps}
              className={clsx(['text-brand-primary-600 hover:underline'])}
            >
              Choose file(s)
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

export function FilePickerName({
  file,
  children,
  ...props
}: Readonly<{
  file: File;
} & React.ComponentPropsWithoutRef<'div'>>) {
  const api = useFilePicker();
  const mergedProps = mergeProps(api.getItemNameProps({ file }), props);
  return <div {...mergedProps}>{children}</div>;
}

export const tofilePickerField = (opts?: { maxSizeInKB: number }) => {
  return z.object({
    file: z
      .instanceof(File)
      .refine((fileItem) => {
        if (!opts?.maxSizeInKB) {
          return true;
        }
        const size = fileItem.size;
        if (size === 0) return false;
        const sizeInKB = (size / 1024).toFixed(1);

        return Number(sizeInKB) <= opts.maxSizeInKB;
      }, 'File too large')
      .optional(),
    url: z
      .string()
      .url()
      .nullish()
      .transform((x) => x ?? undefined),
  });
};

const filePickerFieldSchema = tofilePickerField();

export function FilePickerList({
  children,
  emptyMessage = null,
}: Readonly<{
  children: (
    arg: Omit<z.infer<typeof filePickerFieldSchema>, 'file'> & {
      file: File;
    } & Pick<
      UseFilePickerReturn,
      'getFileSize' | 'focused' | 'dragging' | 'clearFiles' | 'deleteFile'
    >,
  ) => React.ReactNode;
  emptyMessage?: React.ReactNode;
}>) {
  const api = useFilePicker();
  if (api.acceptedFiles.length === 0) {
    return emptyMessage;
  }

  return (
    <ul
      {...api.itemGroupProps}
      className={clsx([
        'divide-y rounded-md overflow-y-auto',
        api.acceptedFiles.length !== 0 ? 'border' : '',
      ])}
    >
      {api.acceptedFiles.map((file) => (
        <li key={file.name} {...api.getItemProps({ file })}>
          {children({ file, url: '', ...api })}
        </li>
      ))}
    </ul>
  );
}

export type FilePickerListItemProps = {
  item: Omit<z.infer<typeof filePickerFieldSchema>, 'file'> & {
    file: File;
  } & Pick<
    UseFilePickerReturn,
    'getFileSize' | 'focused' | 'dragging' | 'clearFiles' | 'deleteFile'
  >;
};
export function FilePickerListItem({ item }: Readonly<FilePickerListItemProps>) {
  return (
    <div className='grid gap-2 px-4 py-2'>
      <div className='flex items-center gap-2'>
        <div className='max-w-12 flex items-center justify-center'>
          <FilePickerPreview file={item.file} />
        </div>
        <Typography level='body-sm'>
          <FilePickerName file={item.file}>{item.file.name}</FilePickerName>
          <div>{item.getFileSize(item.file)}</div>
        </Typography>
        <div className='ml-auto'>
          <Button
            size='sm'
            variant='plain'
            color='danger'
            onClick={() => item.deleteFile(item.file)}
          >
            Delete File
          </Button>
        </div>
      </div>
    </div>
  );
}
export function FilePickerPreview({ file }: Readonly<{ file: File }>) {
  const [url, setUrl] = useState<string>('');
  const api = useFilePicker();
  useEffect(() => {
    api.createFileUrl(file, (imageUrl) => setUrl(imageUrl));
  }, [api, file]);

  return <img src={url} alt='' className='w-full h-full object-cover' />;
}
